import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Paper,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableCell,
  TableBody,
  TableRow,
  Chip,
  Modal,
  Fade,
  Grid,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import useSWR from 'swr';

import { secondarySentence } from '../../../assets/styles/text.style';
import { axiosInstance } from '../../../assets/utils/axiosInstance';
import { getMapValue, getResponseStyle } from '../../../assets/utils/helpers';
import { intlFormatRfiStatus } from '../../../constants/reportsRfiIntl';
import { useLocalizedDepartmentData } from '../../../hooks/fetcher/useDepartmentData';
import { AttachmentObject } from '../../../interfaces/AttachmentObject';
import StackPagination from '../../common/stackPagination';
import AttachedItem from '../reports/reportDocuments/attachments/attachedItem';

import { getUrlString } from '~/Page/customer/customerDashboard';
import { useIntakeChannel } from '~/slices/intakeChannelSlice';

const chipStyle = {
  responded: {
    backgroundColor: '#D2F6E4 !important',
    color: '#1ED07A !important',
  },
  viewed: {
    backgroundColor: '#E7E7E7 !important',
    color: '#878787 !important',
  },
  pending: {
    backgroundColor: '#FFEFD7 !important',
    color: '#FF9900 !important',
  },
  closed: {
    backgroundColor: '#FFF1F1 !important',
    color: '#F83B3B !important',
  },

  chipGeneral: {
    fontFamily: 'Schibsted Grotesk',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: 'normal',
  },
};

interface RfiAttachment {
  id: number;
  url: string;
  file_type: string;
  original_file_name: string;
}

interface RfiData {
  id: number;
  case_manager: string;
  due_date_formatted_date: string;
  department_name: string;
  status: number;
}

interface ResponseModalProps {
  id: number;
  open: boolean;
  handleClose: () => void;
  handleCloseAllModal: () => void;
  enableBack?: boolean;
}

interface RequestForInformationProps {
  url?: string;
  children?: React.ReactNode;
  handleClose?: () => void;
}

const useChipStyles = makeStyles(chipStyle);

const IS_ATTACHMENT = 0;

const fetcher = (url: string) =>
  axiosInstance()
    .get(url)
    .then((res) => res.data);

const ResponseModal = ({
  id,
  open,
  handleClose,
  enableBack = false,
  handleCloseAllModal,
}: ResponseModalProps) => {
  const { data, isLoading, error } = useSWR(
    `tenant/request-for-information/${id}/details`,
    fetcher,
  );
  const { getLocalizedDepartmentName } = useLocalizedDepartmentData();

  return (
    <Modal
      open={open}
      onClose={() => {
        handleClose();
      }}
      closeAfterTransition
    >
      <Fade in={open}>
        <Box
          component={Paper}
          sx={{
            position: 'absolute' as const,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '75%',
            p: 3,
            outline: 'none',
          }}
        >
          <IconButton
            sx={{
              position: 'absolute',
              float: 'right',
              top: 15,
              right: 10,
            }}
            onClick={() => {
              handleCloseAllModal();
            }}
          >
            <CloseIcon />
          </IconButton>
          {isLoading && <CircularProgress size={20} />}
          {!isLoading && error && (
            <Typography color='error'>{error.response.data.message}</Typography>
          )}
          {!isLoading && data && (
            <>
              {enableBack && (
                <IconButton
                  sx={{
                    float: 'left',
                    top: -6,
                    mr: 1,
                  }}
                  onClick={() => {
                    handleClose();
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
              )}
              <Typography sx={{ fontSize: 16, fontWeight: 500 }}>
                <FormattedMessage defaultMessage='Response' />
              </Typography>
              {/* Recipient Information */}
              <Box
                sx={{
                  mt: 2,
                  flexDirection: 'row',
                }}
              >
                <Grid container>
                  <Grid item xs={9}>
                    <Grid container>
                      <Grid item xs={4}>
                        <Typography
                          component='div'
                          sx={{ fontSize: 16, py: 1 }}
                        >
                          <Typography
                            component='span'
                            sx={{ mr: 1, ...secondarySentence }}
                          >
                            <FormattedMessage defaultMessage="Recipient's Name:" />
                          </Typography>
                          {data.recipient_name}
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography
                          component='div'
                          sx={{ fontSize: 16, py: 1 }}
                        >
                          <Typography
                            component='span'
                            sx={{ mr: 1, ...secondarySentence }}
                          >
                            <FormattedMessage defaultMessage='Department:' />
                          </Typography>
                          {getLocalizedDepartmentName(data.department_name)}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography
                          component='div'
                          sx={{ fontSize: 16, py: 1 }}
                        >
                          <Typography
                            component='span'
                            sx={{ mr: 1, ...secondarySentence }}
                          >
                            <FormattedMessage defaultMessage='Recipients Email:' />
                          </Typography>
                          {data.recipient_email}
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography
                          component='div'
                          sx={{ fontSize: 16, py: 1 }}
                        >
                          <Typography
                            component='span'
                            sx={{ mr: 1, ...secondarySentence }}
                          >
                            <FormattedMessage defaultMessage='Due Date:' />
                          </Typography>
                          {data.due_date_formatted_date}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography
                          component='div'
                          sx={{ fontSize: 16, py: 1 }}
                        >
                          <Typography
                            component='span'
                            sx={{ mr: 1, ...secondarySentence }}
                          >
                            <FormattedMessage defaultMessage='Subject:' />
                          </Typography>
                          {data.subject}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={3}>
                    {data.status === 4 && (
                      <Grid>
                        <Typography
                          sx={{
                            color: '#F83B3B',
                            fontSize: 14,
                          }}
                        >
                          <FormattedMessage defaultMessage='Request Closed' />
                        </Typography>
                        <Box mt={2}>
                          <Typography
                            sx={{
                              color: '#878787',
                              fontSize: 14,
                            }}
                          >
                            <FormattedMessage defaultMessage='Closure reason:' />
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 14,
                            }}
                          >
                            {data.closure_reason}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Box>
              {/* Details */}
              <Box
                sx={{
                  mt: 1,
                  mb: 2,
                  fontSize: 14,
                  height: '390px',
                  overflowY: 'scroll',
                }}
              >
                {data.details}
              </Box>
              {/* Attachments */}
              <Box>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  <FormattedMessage defaultMessage='Attachments' />
                </Typography>
                <Box>
                  {data.files.length != 0 ? (
                    <Grid container>
                      {data.files.map((file: RfiAttachment) => {
                        const attachmentObject: AttachmentObject = {
                          id: file.id,
                          url: file.url,
                          fileType: file.file_type,
                          name: file.original_file_name,
                          downloadAttachmentsFlag: true,
                          attachmentType: IS_ATTACHMENT,
                          userType: null,
                        };
                        return (
                          <Grid item xs={2} key={file.id}>
                            <AttachedItem {...attachmentObject} />
                          </Grid>
                        );
                      })}
                    </Grid>
                  ) : (
                    <Typography
                      sx={{
                        ...secondarySentence,
                        mt: 1,
                      }}
                    >
                      <FormattedMessage defaultMessage='No attachment' />
                    </Typography>
                  )}
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Fade>
    </Modal>
  );
};

const RequestForInformation = ({
  url = 'dashboard/request-for-information',
  children,
  handleClose,
}: RequestForInformationProps) => {
  const chipClasses = useChipStyles();
  const [api, setApi] = useState(url);
  const [rfiID, setRfiID] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const selectedChannel = useIntakeChannel().intakeChannelData.selectedChannel;
  const fullURL = getUrlString(api, '', '', '', -1, selectedChannel);
  const {
    data: rfi,
    isLoading: rfiIsLoading,
    error: rfiError,
  } = useSWR(fullURL, fetcher);
  const [openResponseModal, setOpenResponseModal] = useState(false);
  const { getLocalizedDepartmentName } = useLocalizedDepartmentData();

  useEffect(() => {
    setApi(url);
  }, [url]);

  const getChipClass = (value: number) => {
    switch (value) {
      case 1:
        return chipClasses.pending;
      case 2:
        return chipClasses.viewed;
      case 3:
        return chipClasses.responded;
      case 4:
        return chipClasses.closed;
      default:
        return chipClasses.pending;
    }
  };

  const handleOpenResponseModal = (id: number) => {
    setRfiID(id);
    setOpenResponseModal(true);
  };

  const handleCloseResponseModal = () => {
    setOpenResponseModal(false);
  };

  const handleCloseAllModal = () => {
    setOpenResponseModal(false);
    if (handleClose) {
      handleClose();
    }
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setCurrentPage(newPage);
    setApi(
      newPage > currentPage
        ? rfi.next.split('/api/')[1]
        : rfi.previous.split('/api/')[1],
    );
  };

  if (rfiError)
    return (
      <Typography color='error'>{rfiError.response.data.message}</Typography>
    );

  return (
    <>
      <Box component={Paper} variant='outlined'>
        <Box p={2}>
          <Typography fontSize={18} fontWeight={500} mb={1}>
            <FormattedMessage defaultMessage='Request For Information' />
          </Typography>
          {children}
          {!rfiIsLoading && (
            <Typography
              sx={{
                alignContent: 'center',
                display: 'flex',
                alignItems: 'center',
                ...secondarySentence,
              }}
              component='div'
            >
              <Typography
                component='span'
                sx={{
                  fontSize: 28,
                  fontWeight: 500,
                  color: '#181818',
                  marginRight: 1,
                }}
              >
                {rfi.count}
              </Typography>
              <FormattedMessage defaultMessage='Total RFI' />
            </Typography>
          )}
        </Box>
        <Box>
          <TableContainer>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography
                      sx={{ color: '#878787', fontSize: 14, fontWeight: 500 }}
                    >
                      <FormattedMessage defaultMessage='Case Manager' />
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      sx={{ color: '#878787', fontSize: 14, fontWeight: 500 }}
                    >
                      <FormattedMessage defaultMessage='Due Date' />
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      sx={{ color: '#878787', fontSize: 14, fontWeight: 500 }}
                    >
                      <FormattedMessage defaultMessage='Department' />
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      sx={{ color: '#878787', fontSize: 14, fontWeight: 500 }}
                    >
                      <FormattedMessage defaultMessage='Status' />
                    </Typography>
                  </TableCell>
                  <TableCell align='center'>
                    <Typography
                      sx={{ color: '#878787', fontSize: 14, fontWeight: 500 }}
                    >
                      <FormattedMessage defaultMessage='Responses' />
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rfiIsLoading && (
                  <TableRow>
                    <TableCell align='center' colSpan={5}>
                      <CircularProgress size={20} />
                    </TableCell>
                  </TableRow>
                )}
                {!rfiIsLoading && rfi.results.length != 0 ? (
                  rfi.results.map((item: RfiData) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.case_manager}</TableCell>
                      <TableCell>{item.due_date_formatted_date}</TableCell>
                      <TableCell>
                        {getLocalizedDepartmentName(item.department_name)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          className={`${getChipClass(item.status)} ${chipClasses.chipGeneral
                            }`}
                          label={intlFormatRfiStatus(getMapValue(item.status))}
                        />
                      </TableCell>
                      <TableCell align='center'>
                        {item.status === 3 || item.status === 4 ? (
                          <Typography
                            component='span'
                            sx={{
                              ...getResponseStyle(item.status),
                              cursor: 'pointer',
                            }}
                            onClick={() => handleOpenResponseModal(item.id)}
                          >
                            <FormattedMessage defaultMessage='Open' />
                          </Typography>
                        ) : (
                          <Typography
                            component='span'
                            sx={getResponseStyle(item.status)}
                          >
                            <FormattedMessage defaultMessage='Waiting' />
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell align='center' colSpan={5}>
                      <Typography
                        sx={{
                          ...secondarySentence,
                          mt: 1,
                        }}
                      >
                        <FormattedMessage defaultMessage='Empty request for information' />
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
      {openResponseModal && (
        <ResponseModal
          id={rfiID}
          open={openResponseModal}
          handleClose={handleCloseResponseModal}
          handleCloseAllModal={handleCloseAllModal}
          enableBack={children != null}
        />
      )}
      {!rfiIsLoading && (
        <Box>
          <StackPagination
            count={rfi.count}
            currentPage={currentPage}
            onPageChange={handlePageChange}
          />
        </Box>
      )}
    </>
  );
};

export default RequestForInformation;
